import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  styled,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../../assets/images/logo.png";
import usePasswordReset from "../../../hooks/api-request/usePasswordReset";
import { useTranslation } from "react-i18next";

const AuthWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  backgroundColor: "#fff",
  overflow: "hidden",
}));

const AuthCard = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 450,
  margin: "auto",
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: -200,
    right: -200,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    zIndex: 0,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -200,
    left: -200,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    zIndex: 0,
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const ContentBox = styled(Box)({
  width: "100%",
  position: "relative",
  zIndex: 1,
  backgroundColor: "#fff",
  borderRadius: 24,
  padding: "32px 24px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
});

const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 24,
  "& img": {
    height: 40,
  },
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: 16,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: "#F5F5F5",
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const BackToHomeLink = styled(Link)(({ theme }) => ({
  position: "absolute",
  top: 20,
  left: 20,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textDecoration: "none",
  fontSize: "0.9rem",
  padding: "8px 16px",
  borderRadius: 20,
  transition: "all 0.2s ease",
  zIndex: 2,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.text.primary,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",

  "&:hover": {
    backgroundColor: "#fff",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const ArrowIcon = styled("span")({
  display: "inline-block",
  marginRight: "4px",
  transition: "transform 0.2s ease",
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const {
    requestPasswordReset,
    loading,
    requestResponseData,
    requestSuccess,
    requestError,
    RequestSnackbar,
  } = usePasswordReset();

  // Redirect to verify OTP page if request is successful
  useEffect(() => {
    if (requestSuccess) {
      setTimeout(() => {
        navigate("/verify-otp", {
          state: { email },
        });
      }, 2000);
    }
  }, [requestSuccess, navigate, email]);

  // Set error from backend
  useEffect(() => {
    if (requestError) {
      setError(
        requestError.message || "An error occurred during password reset",
      );
    }
  }, [requestError]);

  const validateEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!email) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setError("");
    requestPasswordReset(email);
  };

  return (
    <AuthWrapper dir={isRtl ? 'rtl' : 'ltr'}>
      <BackToHomeLink to="/">
        <ArrowIcon>←</ArrowIcon>
        {t('auth.forgotPassword.backToHome')}
      </BackToHomeLink>

      <AuthCard>
        <ContentBox>
          <LogoContainer>
            <img src={Logo} alt="Logo" />
          </LogoContainer>

          <Typography variant="h5" align="center" gutterBottom fontWeight={600}>
            {t('auth.forgotPassword.title')}
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="textSecondary"
            sx={{ mb: 3 }}
          >
            {t('auth.forgotPassword.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {requestSuccess && requestResponseData && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {requestResponseData.detail ||
                t('auth.forgotPassword.messages.success')}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <StyledTextField
              autoFocus
              fullWidth
              placeholder={t('auth.forgotPassword.fields.email')}
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!error && error.includes("email")}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 2,
                height: 48,
                borderRadius: 2,
                position: "relative",
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              ) : (
                t('auth.forgotPassword.buttons.send')
              )}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {t('auth.forgotPassword.rememberPassword.text')}{" "}
              <Link
                to="/login"
                style={{ color: theme.palette.primary.main, fontWeight: 500 }}
              >
                {t('auth.forgotPassword.rememberPassword.link')}
              </Link>
            </Typography>
          </Box>
        </ContentBox>
      </AuthCard>

      {RequestSnackbar}
    </AuthWrapper>
  );
};

export default ForgotPassword;
